import React from "react";
import { Button } from "../../components/Form";
import { BiPlus } from "react-icons/bi";
import { FiEye } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { RiDeleteBin6Line } from "react-icons/ri";
import MedicalRecodModal from "../../components/Modals/MedicalRecodModal";
import { useNavigate } from "react-router-dom";
import NewMedicalRecode from "./NewMedicalRecode";
import axios from "axios";
import { BaseUrl } from "../../BaseUrl";
import { medicalRecodData } from "../../components/Datas";

function MedicalRecord({ isMRMO, setIsMRMO, patientId }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [datas, setDatas] = React.useState({});
  const [medicalRecords, setMedicalRecords] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  // Fetch medical records
  React.useEffect(() => {
    const fetchMedicalRecords = async () => {
      if (!patientId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${BaseUrl}/doctor/medical-records/${patientId}`,
          {
            withCredentials: true,
          }
        );

        if (response.data?.data && Array.isArray(response.data.data)) {
          setMedicalRecords(response.data.data);
        } else {
          setMedicalRecords([]);
        }
      } catch (error) {
        // If 404, it means no records found - that's okay
        if (error.response?.status === 404) {
          setMedicalRecords([]);
        } else {
          console.error("Error fetching medical records:", error);
          toast.error(error.response?.data?.message || "Failed to load medical records");
          setMedicalRecords([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMedicalRecords();
  }, [patientId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
      {
        // Modal
        isOpen && (
          <MedicalRecodModal
            closeModal={() => {
              setIsOpen(false);
              setDatas({});
            }}
            isOpen={isOpen}
            datas={datas}
          />
        )
      }
      <NewMedicalRecode
        closeModal={() => {
          setIsMRMO(false);
        }}
        isOpen={isMRMO}
        patientId={patientId}
      />

      {loading ? (
        <div className='flex items-center justify-center py-10'>
          <p className='text-textGray'>Loading medical records...</p>
        </div>
      ) : (
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          {(medicalRecords.length > 0 ? medicalRecords : medicalRecodData).map((record) => {
            // Use mock data structure if no real records
            const isMockData = medicalRecords.length === 0;
            
            return (
              <div
                key={isMockData ? record.id : (record._id || record.id)}
                className='flex flex-col overflow-hidden rounded-xl border border-border bg-white shadow-md'
              >
                {/* Attachment image */}
                {isMockData ? (
                  record.attachments && record.attachments.length > 0 && (
                    <img
                      src={record.attachments[0]}
                      alt='Medical Attachment'
                      className='h-40 w-full object-cover'
                    />
                  )
                ) : (
                  record.attachments && record.attachments.length > 0 && (
                    <img
                      src={Array.isArray(record.attachments) ? record.attachments[0] : record.attachments}
                      alt='Medical Attachment'
                      className='h-40 w-full object-cover'
                    />
                  )
                )}

                <div className='flex flex-col gap-3 p-4'>
                  {/* upper header part */}
                  <div className='flex items-center justify-between'>
                    <h2 className='text-sm font-semibold text-main'>
                      {isMockData ? record.date : formatDate(record.createdAt || record.date)}
                    </h2>
                    <span className='rounded-md bg-gray-200 px-2 py-1 text-[10px] text-gray-600'>
                      Record
                    </span>
                  </div>

                  {/* Information Section */}
                  <div className='flex flex-col gap-1'>
                    {isMockData ? (
                      record.data?.map((item) => (
                        <p key={item.id} className='text-xs text-gray-700'>
                          <span className='font-medium text-main'>{item.title}:</span>{" "}
                          {item.value?.length > 40
                            ? `${item.value.slice(0, 40)}...`
                            : item.value}
                        </p>
                      ))
                    ) : (
                      <>
                        {record.diagnosis && (
                          <p className='text-xs text-gray-700'>
                            <span className='font-medium text-main'>Diagnosis:</span>{" "}
                            {record.diagnosis.length > 40
                              ? `${record.diagnosis.slice(0, 40)}...`
                              : record.diagnosis}
                          </p>
                        )}
                        {record.chiefComplaint && (
                          <p className='text-xs text-gray-700'>
                            <span className='font-medium text-main'>Chief Complaint:</span>{" "}
                            {record.chiefComplaint.length > 40
                              ? `${record.chiefComplaint.slice(0, 40)}...`
                              : record.chiefComplaint}
                          </p>
                        )}
                        {record.notes && (
                          <p className='text-xs text-gray-700'>
                            <span className='font-medium text-main'>Notes:</span>{" "}
                            {record.notes.length > 40
                              ? `${record.notes.slice(0, 40)}...`
                              : record.notes}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* lower section part */}
                  {record.amount && (
                    <div className='mt-2 flex items-center justify-between'>
                      <p className='text-sm font-semibold text-subMain'>
                        <span className='text-[11px] text-gray-500'>(Tsh)</span>{" "}
                        {record.amount}
                      </p>

                      <div className='flex gap-2'>
                        <button
                          onClick={() => {
                            setIsOpen(true);
                            setDatas(record);
                          }}
                          className='flex items-center justify-center rounded-md border border-border bg-white p-2 text-subMain transition duration-200 hover:bg-subMain hover:text-white'
                        >
                          <FiEye className='text-lg' />
                        </button>
                        <button
                          onClick={() => {
                            toast.error("This feature is not available yet");
                          }}
                          className='flex items-center justify-center rounded-md border border-border bg-white p-2 text-red-500 transition duration-200 hover:bg-red-500 hover:text-white'
                        >
                          <RiDeleteBin6Line className='text-lg' />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default MedicalRecord;
